import React, { useEffect, useRef, useState } from "react";
import { downloadElementAsPdf } from "../../lib/contractPdf";
import "./VehicleDocumentModal.css";

// The mockup's document modal (DocumentPreviewDialog, "paper" kind) for the vehicle record's
// demonstration documents: same shell as ContractPreviewModal (scrim, sticky header with title,
// download and close, one scroll surface), showing the Klargo paper built by vehicleDocuments.js.
const Block = ({ block }) => {
  switch (block.kind) {
    case "title":
      return (
        <div className="vdoc-title-block">
          <h1 className="vdoc-h1">{block.title}</h1>
          <p className="vdoc-sub">{block.subtitle}</p>
          <div className="vdoc-rule" />
        </div>
      );
    case "meta":
    case "fields":
      return (
        <div>
          {block.heading ? <h2 className="vdoc-h2">{block.heading}</h2> : null}
          <div className="vdoc-rows">
            {block.fields.map((f) => (
              <div key={f.label} className="vdoc-row">
                <span className="vdoc-row-label">{f.label}</span>
                <span className="vdoc-row-value">{f.value}</span>
              </div>
            ))}
          </div>
        </div>
      );
    case "clause":
      return (
        <div>
          {block.heading ? <h2 className="vdoc-h2">{block.heading}</h2> : null}
          <div className="vdoc-paras">
            {block.paragraphs.map((p) => (
              <p key={p} className="vdoc-p">{p}</p>
            ))}
          </div>
        </div>
      );
    case "note":
      return (
        <div className="vdoc-paras">
          {block.paragraphs.map((p) => (
            <p key={p} className="vdoc-note">{p}</p>
          ))}
        </div>
      );
    default:
      return null;
  }
};

export default function VehicleDocumentModal({ t, tp, paper, fileName, onClose }) {
  const modalRef = useRef(null);
  const closeRef = useRef(null);
  const printRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const open = Boolean(paper);

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 60);
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (opener instanceof HTMLElement && opener.isConnected) window.setTimeout(() => opener.focus(), 0);
    };
  }, [open, onClose]);

  const trapTab = (event) => {
    if (event.key !== "Tab") return;
    const root = modalRef.current;
    if (!root) return;
    const focusable = Array.from(root.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])')).filter((el) => el.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleDownload = async () => {
    if (!printRef.current || busy) return;
    setBusy(true);
    try {
      await downloadElementAsPdf(printRef.current, fileName);
    } catch (err) {
      console.error("PDF download failed", err);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="ds-scope ds-animate-veil ds-viewer-scrim fixed inset-0 z-[80] flex" data-testid="document-viewer">
      <div role="dialog" aria-modal="true" aria-label={paper.title} ref={modalRef} onKeyDown={trapTab} data-testid="document-preview" className="ds-viewer-modal ds-animate-layer">
        <div data-testid="viewer-toolbar" className="ds-viewer-modal-header flex shrink-0 items-center gap-3 px-3.5 min-[600px]:px-5">
          <span data-testid="viewer-title" className="min-w-0 flex-1 truncate text-[15px] font-semibold leading-none text-ds-ink">
            {paper.title}
          </span>
          <button type="button" data-testid="viewer-download" onClick={handleDownload} disabled={busy} className="ds-viewer-download">
            <svg width="15" height="15" viewBox="0 0 14 14" aria-hidden="true" style={{ fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" }}>
              <path d="M7 1.6v7.2" />
              <path d="M4 6.2 7 9.2l3-3" />
              <path d="M2 11.4h10" />
            </svg>
            <span>{t("actions.downloadPdf")}</span>
          </button>
          <button type="button" ref={closeRef} data-testid="viewer-close" onClick={onClose} aria-label={t("dealDetails.action.close")} className="ds-viewer-close">
            <svg width="15" height="15" viewBox="0 0 14 14" aria-hidden="true" style={{ fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round" }}>
              <line x1="2" y1="2" x2="12" y2="12" />
              <line x1="12" y1="2" x2="2" y2="12" />
            </svg>
          </button>
        </div>

        <div data-testid="viewer-scroll" className="ds-viewer-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-5 min-[600px]:px-7 min-[600px]:py-7">
          <div ref={printRef} className="klargo-public vdoc-wrap">
            <article className="ds-paper-doc vdoc-page" data-testid="contract-preview-body">
              <div className="ds-paper-watermark" aria-hidden="true">
                <div className="ds-paper-watermark-text">KLARGO</div>
              </div>
              <div className="vdoc-inner">
                <div className="vdoc-metabar">
                  <div className="vdoc-metabar-row">
                    <span className="vdoc-brand">{paper.brandLine}</span>
                    <span className="vdoc-ref">{paper.referenceLine}</span>
                  </div>
                  <div className="vdoc-rule vdoc-rule--bar" />
                </div>
                <div className="vdoc-blocks">
                  {paper.blocks.map((b, i) => (
                    <Block key={i} block={b} />
                  ))}
                </div>
                <div className="vdoc-footer">
                  <span>{paper.footerLine}</span>
                  <span>{tp("contract.preview.pageOf").replace("{page}", "1").replace("{total}", "1")}</span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>
  );
}
